import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import { IntercomProvider } from "react-use-intercom";
import { AlertMessages, CustomMessageProvider } from "go-alert/AlertMessage";
import { AppErrorBoundary } from "go-component/components/AppErrorBoundary";
import { LoadingProvider } from "go-core/components/Loading";
import ScrollToTop from "go-core/components/ScrollToTop";
import { useCustomExceptionHandlers } from "go-core/hooks";
import { useMomentLocaleForUser } from "go-core/hooks/useMomentLocale";
import { ConfirmationServiceProvider } from "go-form/components/ModalConfirm";
import ParametersSecurity from "go-security/services";
import { restSecurityApi } from "go-security/services/Api/api";
import { refreshTokenApi } from "go-security/services/Api/applyAppTokenRefreshInterceptor";
import { AuthCredentialsService } from "go-security/services/AuthCredentialsService";
import { IntercomSecurity } from "go-security/services/intercom/intercomSecurity";
import { AuthCredentials } from "go-security/services/types";
import { selectUser } from "go-security/services/users/selectors";
import Routes from "./Routes";
import { useSentry } from "./app/crashreport/useSentry";
import GoogleAnalytics from "./components/Helmet/GoogleAnalytics";
import { ReactComponent as LoadingSVG } from "./images/svg/loading.svg";
import { exceptionHandlers } from "./utils/customExceptionHandler/CustomExceptionHandler";
import { getApiURL, getGoAccountsApiURL, getSentryDSN, getTokenClientId, getTokenClientSecret } from "./utils/envutil";

const accountsUrl = getGoAccountsApiURL();

AuthCredentialsService.init({
	clientId: getTokenClientId(),
	secretId: getTokenClientSecret(),
	url: getApiURL(),
	accountsUrl,
} as AuthCredentials);
restSecurityApi.defaults.baseURL = `${getApiURL()}/ajax/`;
refreshTokenApi.defaults.baseURL = `${getApiURL()}/`;
ParametersSecurity.setLoginRedirectUrl(accountsUrl);
ParametersSecurity.setIframeUrl(accountsUrl);
ParametersSecurity.setAccountsUrl(accountsUrl);
ParametersSecurity.setAppName("GoStock");

function App() {
	const user = useSelector(selectUser);
	const { t } = useTranslation();
	const permissionIds = [
		"PRODUCT_BASIC",
		"PRODUCT_EDIT",
		"PRODUCT_LINKS",
		"PRODUCT_MANAGE",
		"PURCHASE_ORDER_BASIC",
		"PURCHASE_ORDER_EDIT",
		"PURCHASE_ORDER_RECEIVE",
		"PURCHASE_ORDER_MANAGE",
		"PURCHASE_CORRECTION_BASIC",
		"PURCHASE_CORRECTION_EDIT",
		"PURCHASE_CORRECTION_MANAGE",
		"SALES_ORDER_BASIC",
		"SALES_ORDER_EDIT",
		"SALES_ORDER_SHIP",
		"SALES_ORDER_MANAGE",
		"SALES_RETURN_BASIC",
		"SALES_RETURN_EDIT",
		"SALES_RETURN_MANAGE",
		"TRANSFER_ORDER_BASIC",
		"TRANSFER_ORDER_EDIT",
		"TRANSFER_ORDER_MANAGE",
		"STOCK_ADJUSTMENT_BASIC",
		"STOCK_ADJUSTMENT_EDIT",
		"STOCK_ADJUSTMENT_MANAGE",
		"BUNDLE_BASIC",
		"BUNDLE_EDIT",
		"BUNDLE_MANAGE",
		"STOCKTAKING_BASIC",
		"STOCKTAKING_DRAFT",
		"STOCKTAKING_SUMMARY",
		"STOCKTAKING_CLOSE",
		"STOCKTAKING_MANAGE",
		"EDIT_NOT_OWNED_DOCUMENT",
		"VIEW_NOT_OWNED_DOCUMENT",
		"CONTACT_BASIC",
		"CONTACT_EDIT",
		"CONTACT_LINKS",
		"CONTACT_MANAGE",
		"REPORT",
		"INVOICE_BASIC",
		"INVOICE_DRAFT_EDIT",
		"INVOICE_CLOSE",
		"INVOICE_MANAGE",
		"COST_PREVIEW",
		"STOCK_STATES_PREVIEW",
		"SETTINGS",
		"MANAGE",
		"WAREHOUSE_API",
	];
	const permissions = permissionIds.map((id) => ({
		id,
		title: t(`enums.permissions.value.${id}`),
	}));

	useCustomExceptionHandlers(exceptionHandlers);
	const sentryDSN = getSentryDSN();
	useSentry(sentryDSN);
	useMomentLocaleForUser(selectUser);

	return (
		<div className="App">
			<GoogleAnalytics />
			<IntercomProvider autoBoot={false} appId={IntercomSecurity.getIntercomId(user)}>
				<BrowserRouter>
					<AppErrorBoundary>
						<CustomMessageProvider>
							<ConfirmationServiceProvider>
								<LoadingProvider icon={<LoadingSVG />}>
									<AlertMessages permissions={permissions} />
									<ScrollToTop />
									<Routes />
								</LoadingProvider>
							</ConfirmationServiceProvider>
						</CustomMessageProvider>
					</AppErrorBoundary>
				</BrowserRouter>
			</IntercomProvider>
		</div>
	);
}

export default App;
